import { useAtomValue } from 'jotai'
import { userTeamAtom } from '../../../store/user.store'
import { useUser } from '../index'
import {
  hasMinimumDashboardRole,
  hasDashboardPermission,
} from '../model/dashboard-permissions'
import type { UserRole } from '~shared/types'

/**
 * Hook to check if the current user has specific permissions or role
 * privileges.
 *
 * @deprecated Use `useDashboardAccess` instead, which uses React Query directly
 * and provides loading state via `isReady`. This hook reads from Jotai atoms
 * which are being phased out.
 *
 * @example
 *   // Migration example:
 *   // Before:
 *   const canEdit = useDashboardPermission({ role: UserRole.TEAM_EDITOR })
 *
 *   // After:
 *   const { isReady, hasRole } = useDashboardAccess({ teamId })
 *   const canEdit = isReady && hasRole(UserRole.TEAM_EDITOR)
 *
 *   This hook uses a bitmask permission system where roles are mapped to permission flags.
 *   You can check either by minimum role level or by specific permission flags.
 *
 * @param params - Configuration object
 * @param params.role - Check if user has at least this role level (hierarchical
 *   check)
 * @param params.permissions - Check if user has specific permission flags
 *   (bitmask check)
 *
 * @returns `true` if user has the required permissions/role, `false` otherwise
 *
 * @see {@link useDashboardAccess} for the recommended replacement
 * @see {@link DashboardPermissions} for available permission flags
 * @see {@link UserRole} for available roles
 */
export const useDashboardPermission = ({
  role,
  permissions,
}: {
  permissions?: number
  role?: UserRole
}): boolean => {
  if (role !== undefined && permissions !== undefined) {
    throw new Error(
      '[useUserPermission] Cannot specify both `role` and `permissions`.',
    )
  }

  const userTeam = useAtomValue(userTeamAtom)
  const { data: user } = useUser()

  const userUid = user?.uid

  if (!userTeam || !userUid) return false

  const teamMember = userTeam.members[userUid]
  const memberRole = teamMember?.role

  if (!memberRole) return false

  if (role) {
    return hasMinimumDashboardRole(memberRole, role)
  }

  if (permissions) {
    return hasDashboardPermission(memberRole, permissions)
  }

  return false
}
